'use client'

import { Box, Flex, Grid, Table, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { SectionErrorBoundary } from '@/components/error/boundary'
import { getCurrencyInfo } from '@/configs/currencies'
import { useExchangeRates } from '@/services/currency'
import { CoinOverall } from '@/types/coin'
import { AllAvailableCurrencyCodes } from '@/types/currency'
import { currencySymbolMap } from '@/utils/number/currencyFormatter'
import { formatEffectiveNumber, formatPercentageColor } from '@/utils/number/format'

import { HistoricalPricesResult } from './HistoricalPriceData'

const UNITS = [0.5, 1, 5, 10, 50, 100, 500, 1000]

interface HistoricalPriceTableProps {
  currency: AllAvailableCurrencyCodes
  coin: CoinOverall
  historicalPrices: HistoricalPricesResult
}

interface PriceTableSectionProps {
  title: string
  percentageChange: number
  currentDate: string
  agoLabel: string
  currentPriceInFiat: number
  historicalPrice: number
  currencySymbol: string
  coinSymbol: string
  amountLabel: string
  targetCurrencyLabel: string
}

const PriceTableSection = ({
  title,
  percentageChange,
  currentDate,
  agoLabel,
  currentPriceInFiat,
  historicalPrice,
  currencySymbol,
  coinSymbol,
  amountLabel,
  targetCurrencyLabel,
}: PriceTableSectionProps) => {
  const formattedPercentage = `${percentageChange > 0 ? '+' : ''}${(percentageChange * 100).toFixed(2)}%`
  const percentageColor = formatPercentageColor(percentageChange)

  return (
    <Box mb={48}>
      <Box mb={16}>
        <Flex align="center" gap={8} fz={16} lh="20px">
          <Text fw={600}>{title}</Text>
          <Text fw={600} c={percentageColor}>
            {formattedPercentage}
          </Text>
        </Flex>
      </Box>

      {/* Table */}
      <Table verticalSpacing={12} horizontalSpacing={24} withRowBorders layout="fixed">
        <Table.Thead>
          <Table.Tr c="var(--content-secondary)" lh="20px" fz={14} fw={600}>
            <Table.Th>{amountLabel}</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>{currentDate}</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>{agoLabel}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {UNITS.map(unit => {
            const currentValue = currentPriceInFiat * unit
            const historicalValue = historicalPrice * unit

            return (
              <Table.Tr key={unit}>
                <Table.Td style={{ fontSize: '14px', fontWeight: 600 }}>
                  {unit.toLocaleString()} {coinSymbol}
                </Table.Td>
                <Table.Td
                  fz={14}
                  fw={600}
                  style={{
                    textAlign: 'right',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {currentPriceInFiat > 0
                    ? `${currencySymbol}${formatEffectiveNumber(currentValue)} ${targetCurrencyLabel}`
                    : '-'}
                </Table.Td>
                <Table.Td
                  fz={14}
                  fw={600}
                  style={{
                    textAlign: 'right',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {historicalPrice > 0
                    ? `${currencySymbol}${formatEffectiveNumber(historicalValue)} ${targetCurrencyLabel}`
                    : '-'}
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    </Box>
  )
}

function HistoricalPriceTable({ currency, coin, historicalPrices }: HistoricalPriceTableProps) {
  const t = useTranslations('converter')
  const { data: exchangeRates } = useExchangeRates()

  const fiatRate = useMemo(() => {
    if (!exchangeRates) return 0
    const rate = exchangeRates[currency]
    return rate ? Number(rate) : 0
  }, [exchangeRates, currency])

  const tokenPriceInFiat = (coin.usd_price || 0) * fiatRate
  const currencySymbol = currencySymbolMap[currency] || ''
  const targetCurrency = getCurrencyInfo(currency)
  const targetCurrencyLabel = targetCurrency?.type === 'fiat' ? '' : targetCurrency?.code || ''
  const currentDate = useMemo(() => {
    const now = new Date()
    return `${t('today')} ${now.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })}`
  }, [t])

  const sections = [
    {
      key: '24h',
      title: t('timePriceChange', { time: t('1Day'), symbol: coin.symbol }),
      percentageChange: historicalPrices.day.priceChange,
      agoLabel: t('24HAgo'),
      historicalPrice: historicalPrices.day.price,
    },
    {
      key: '1m',
      title: t('timePriceChange', { time: t('1Month'), symbol: coin.symbol }),
      percentageChange: historicalPrices.month.priceChange,
      agoLabel: t('1MAgo'),
      historicalPrice: historicalPrices.month.price,
    },
    {
      key: '1y',
      title: t('timePriceChange', { time: t('1Year'), symbol: coin.symbol }),
      percentageChange: historicalPrices.year.priceChange,
      agoLabel: t('1YAgo'),
      historicalPrice: historicalPrices.year.price,
    },
  ]

  return (
    <Box>
      <Text component="h2" fz={20} lh="28px" fw={600} mb={32}>
        {t('historicalPriceChange', { token: coin.name })}
      </Text>

      <Grid gutter={{ base: 0, md: 40 }}>
        {sections.map(section => (
          <Grid.Col key={section.key} span={{ base: 12 }}>
            <PriceTableSection
              title={section.title}
              percentageChange={section.percentageChange}
              currentDate={currentDate}
              agoLabel={section.agoLabel}
              amountLabel={t('amount')}
              currentPriceInFiat={tokenPriceInFiat}
              historicalPrice={section.historicalPrice}
              currencySymbol={currencySymbol}
              coinSymbol={coin.symbol}
              targetCurrencyLabel={targetCurrencyLabel}
            />
          </Grid.Col>
        ))}
      </Grid>
    </Box>
  )
}

export default SectionErrorBoundary.with(HistoricalPriceTable)
